import { SearchOutlined } from "@ant-design/icons";
import { Avatar, Flex, Select, Spin, Typography } from "antd";
import { debounce } from "lodash";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGetComplianceClinicianSearchQuery } from "../../service/compliance.service";
import { getRandomAvatarColor } from "../../utils/ui.utils";

type Props = {
  placeholder?: string;
  width?: number | string;
  /** Optional callback when a clinician is selected. Defaults to navigating to detail. */
  onSelect?: (clinicianId: number) => void;
};

const ClinicianSearch = ({ placeholder = "Search clinicians by name or email", width = 320, onSelect }: Props) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");

  const { data, isFetching } = useGetComplianceClinicianSearchQuery({ q: query, limit: 20 }, { skip: false });

  const handleSearch = useMemo(
    () =>
      debounce((value: string) => {
        setQuery(value.trim());
      }, 250),
    [],
  );

  const options = (data?.results ?? []).map((row) => ({
    value: row.clinician_id,
    label: (
      <Flex align="center" gap={10}>
        <Avatar size={28} style={{ backgroundColor: getRandomAvatarColor(row.display_name) }}>
          {row.avatar_initials}
        </Avatar>
        <Flex vertical>
          <Typography.Text strong>{row.display_name}</Typography.Text>
          {row.specialty ? (
            <Typography.Text style={{ fontSize: 11, color: "#6b7280" }}>{row.specialty}</Typography.Text>
          ) : null}
        </Flex>
      </Flex>
    ),
  }));

  return (
    <Select
      showSearch
      placeholder={placeholder}
      style={{ width }}
      suffixIcon={<SearchOutlined />}
      onSearch={handleSearch}
      onChange={(value) => {
        const id = Number(value);
        if (Number.isNaN(id)) return;
        if (onSelect) onSelect(id);
        else navigate(`/compliance/clinicians/${id}`);
      }}
      filterOption={false}
      options={options}
      notFoundContent={isFetching ? <Spin size="small" /> : "No matches"}
      value={undefined}
      allowClear
    />
  );
};

export default ClinicianSearch;
